/**
 * Admin Panel - Travel Tools Management (formerly My Morocco)
 * Reorganized with separate tabs for each feature
 * Stats, Cities, Markers, Shopping, Budget, Phrases, Flagged, Limits, Blocked, Config
 */
import React, { useState, useEffect, useCallback } from 'react';
import AdminTravelToolsWidgets from './AdminTravelToolsWidgets';
import AdminTravelToolsDistance from './AdminTravelToolsDistance';
import AdminTravelToolsGuideCards from './AdminTravelToolsGuideCards';
import AdminImagePicker from './AdminImagePicker';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTravelTools = ({ adminToken }) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Limits
  const [limits, setLimits] = useState({
    markers: 50,
    phrases: 200,
    shopping_lists: 20,
    items_per_list: 50,
    budget_entries: 500,
    wishlist_items: 30
  });
  
  // Flagged Users
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  
  // Blocked Users
  const [blockedUsers, setBlockedUsers] = useState([]);
  
  // Config data
  const [cities, setCities] = useState([]);
  const [phraseCategories, setPhraseCategories] = useState([]);
  const [emojis, setEmojis] = useState([]);
  const [units, setUnits] = useState([]);
  const [shoppingCategories, setShoppingCategories] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  
  // Edit states
  const [editingPhraseCategory, setEditingPhraseCategory] = useState(null);
  const [editingPhraseField, setEditingPhraseField] = useState(null);
  
  // User Limits
  const [userLimits, setUserLimits] = useState({
    max_markers_per_user: 10,
    max_phrases_per_user: 50,
    max_lists_per_user: 5,
    max_items_per_list: 20,
    max_wishlist_per_user: 30,
    max_budget_items_per_user: 100,
    max_phrase_chars: 300
  });
  
  // Phrase fields (custom dictionary fields)
  const [phraseFields, setPhraseFields] = useState([]);
  const [phrasesTitle, setPhrasesTitle] = useState('Make Your Own Dictionary');
  const [shoppingTitle, setShoppingTitle] = useState('Shopping Lists');
  const [budgetTitle, setBudgetTitle] = useState('Budget Tracker');
  const [disclaimerText, setDisclaimerText] = useState('');
  const [draggedField, setDraggedField] = useState(null);
  
  // Guide items
  const [guideItems, setGuideItems] = useState([]);
  const [guideTitle, setGuideTitle] = useState('My Morocco Guide');
  const [guideSubtitle, setGuideSubtitle] = useState('Your personal travel companion');
  const [guideIntro, setGuideIntro] = useState('');
  const [newGuideItem, setNewGuideItem] = useState({ title: '', icon: '📍', content: '' });
  const [editingGuideItem, setEditingGuideItem] = useState(null);
  const [expandedGuideItem, setExpandedGuideItem] = useState(null);
  
  // Form states
  const [newCity, setNewCity] = useState({ name: '', name_darija: '', name_ar: '', lat: '', lng: '', region: '' });
  const [newPhraseCategory, setNewPhraseCategory] = useState({ id: '', name: '', icon: '📝', image_url: '' });
  const [newEmoji, setNewEmoji] = useState({ id: '', emoji: '', name: '', image_url: '' });

  // Image-picker modal state — keyed by which row/form is currently picking.
  //   target shape: { kind: 'newEmoji' | 'editEmoji' | 'newCategory' | 'editCategory', id?: string }
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null);
  // Note: editingEmoji is declared above (line ~100) along with other inline-edit states.
  const [newUnit, setNewUnit] = useState({ id: '', name: '', symbol: '' });
  const [newShoppingCategory, setNewShoppingCategory] = useState({ id: '', name: '', icon: '📦' });
  const [newBudgetCategory, setNewBudgetCategory] = useState({ id: '', name: '', icon: '💰' });
  const [newPhraseField, setNewPhraseField] = useState({ id: '', name: '', icon: '📝' });
  const [blockUserForm, setBlockUserForm] = useState({ user_id: '', user_email: '', reason: '' });
  
  // Visibility settings
  const [visibility, setVisibility] = useState({ is_visible: true, is_pro_only: false });

  // Drag-drop reorder state (admin emoji grid)
  const [dragId, setDragId] = useState(null);

  // Edit states for inline editing
  const [editingCity, setEditingCity] = useState(null);
  const [editingEmoji, setEditingEmoji] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editingShoppingCat, setEditingShoppingCat] = useState(null);
  const [editingBudgetCat, setEditingBudgetCat] = useState(null);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingFieldName, setEditingFieldName] = useState('');
  const [editingFieldIconId, setEditingFieldIconId] = useState(null);
  const [editingFieldIcon, setEditingFieldIcon] = useState('');
  const [fieldsOrderChanged, setFieldsOrderChanged] = useState(false);
  const [savingFieldOrder, setSavingFieldOrder] = useState(false);

  const authHeaders = { 'Authorization': `Bearer ${adminToken}` };

  useEffect(() => {
    loadData();
    loadVisibility();
  }, [activeTab]);

  const loadVisibility = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/visibility`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setVisibility(data);
      }
    } catch (error) {
      console.error('Error loading visibility:', error);
    }
  };

  const toggleVisibility = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/visibility/toggle`, { 
        method: 'POST',
        headers: authHeaders 
      });
      if (res.ok) {
        const data = await res.json();
        setVisibility(prev => ({ ...prev, is_visible: data.is_visible }));
        showMessage(data.message);
      }
    } catch (error) {
      showMessage('Failed to toggle visibility');
    }
  };

  const toggleProOnly = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/visibility/toggle-pro`, { 
        method: 'POST',
        headers: authHeaders 
      });
      if (res.ok) {
        const data = await res.json();
        setVisibility(prev => ({ ...prev, is_pro_only: data.is_pro_only }));
        showMessage(data.message);
      }
    } catch (error) {
      showMessage('Failed to toggle pro-only');
    }
  };

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'stats') {
        const res = await fetch(`${API}/api/travel-tools/admin/stats`, { headers: authHeaders });
        if (res.ok) setStats(await res.json());
      } else if (['config', 'cities', 'markers', 'shopping', 'budget', 'fields', 'disclaimer'].includes(activeTab)) {
        const res = await fetch(`${API}/api/travel-tools/admin/config`, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          setCities(data.cities || []);
          setPhraseCategories(data.phrase_categories || []);
          setEmojis(data.marker_emojis || []);
          setUnits(data.shopping_units || []);
          setShoppingCategories(data.shopping_categories || []);
          setBudgetCategories(data.budget_categories || []);
          setPhraseFields(data.phrase_fields || []);
          setPhrasesTitle(data.phrases_title || 'Make Your Own Dictionary');
          setShoppingTitle(data.shopping_title || 'Shopping Lists');
          setBudgetTitle(data.budget_title || 'Budget Tracker');
        }
        // Load disclaimer separately
        if (activeTab === 'disclaimer') {
          const disclaimerRes = await fetch(`${API}/api/travel-tools/admin/config/disclaimer`, { headers: authHeaders });
          if (disclaimerRes.ok) {
            const disclaimerData = await disclaimerRes.json();
            setDisclaimerText(disclaimerData.disclaimer_text || '');
          }
        }
      } else if (activeTab === 'flagged') {
        const res = await fetch(`${API}/api/travel-tools/admin/flagged-users`, { headers: authHeaders });
        if (res.ok) setFlaggedUsers(await res.json());
      } else if (activeTab === 'blocked') {
        const res = await fetch(`${API}/api/travel-tools/admin/blocked-users`, { headers: authHeaders });
        if (res.ok) setBlockedUsers(await res.json());
      } else if (activeTab === 'limits') {
        const res = await fetch(`${API}/api/travel-tools/admin/config/limits`, { headers: authHeaders });
        if (res.ok) {
          const limitsData = await res.json();
          setLimits(limitsData);
          setUserLimits(limitsData);
        }
      } else if (activeTab === 'guide') {
        const res = await fetch(`${API}/api/travel-tools/admin/guide`, { headers: authHeaders });
        if (res.ok) {
          const guideData = await res.json();
          setGuideItems(guideData.items || []);
          setGuideTitle(guideData.title || 'My Morocco Guide');
          setGuideSubtitle(guideData.subtitle || 'Your personal travel companion');
          setGuideIntro(guideData.intro || '');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const updateLimits = async () => {
    const formData = new FormData();
    Object.entries(userLimits).forEach(([key, value]) => {
      formData.append(key, value);
    });
    const res = await fetch(`${API}/api/travel-tools/admin/config/limits`, { method: 'PUT', body: formData, headers: authHeaders });
    showMessage('Limits updated');
  };

  const resolveFlag = async (flagId) => {
    await fetch(`${API}/api/travel-tools/admin/flagged-users/${flagId}/resolve`, { method: 'PUT', headers: authHeaders });
    showMessage('Flag resolved');
    loadData();
  };

  const deleteFlag = async (flagId) => {
    await fetch(`${API}/api/travel-tools/admin/flagged-users/${flagId}`, { method: 'DELETE', headers: authHeaders });
    showMessage('Flag deleted');
    loadData();
  };

  const blockUser = async () => {
    if (!blockUserForm.user_id || !blockUserForm.user_email) {
      showMessage('User ID and Email required');
      return;
    }
    const formData = new FormData();
    formData.append('user_id', blockUserForm.user_id);
    formData.append('user_email', blockUserForm.user_email);
    formData.append('reason', blockUserForm.reason);
    
    await fetch(`${API}/api/travel-tools/admin/blocked-users`, { 
      method: 'POST', 
      body: formData, 
      headers: authHeaders 
    });
    showMessage('User blocked');
    setBlockUserForm({ user_id: '', user_email: '', reason: '' });
    loadData();
  };

  const unblockUser = async (userId) => {
    await fetch(`${API}/api/travel-tools/admin/blocked-users/${userId}`, { 
      method: 'PUT', 
      headers: authHeaders 
    });
    showMessage('User unblocked');
    loadData();
  };

  // ========== CITIES CRUD ==========
  const addCity = async () => {
    if (!newCity.name) { showMessage('City name required'); return; }
    const formData = new FormData();
    formData.append('name', newCity.name);
    formData.append('name_darija', newCity.name_darija || '');
    formData.append('name_ar', newCity.name_ar || '');
    formData.append('lat', newCity.lat || '0');
    formData.append('lng', newCity.lng || '0');
    formData.append('region', newCity.region || '');
    
    await fetch(`${API}/api/travel-tools/admin/config/cities`, { 
      method: 'POST', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('City added');
    setNewCity({ name: '', name_darija: '', name_ar: '', lat: '', lng: '', region: '' });
    loadData();
  };

  const updateCity = async () => {
    if (!editingCity) return;
    const formData = new FormData();
    formData.append('name', editingCity.name);
    formData.append('name_darija', editingCity.name_darija || '');
    formData.append('name_ar', editingCity.name_ar || '');
    formData.append('lat', editingCity.lat || '0');
    formData.append('lng', editingCity.lng || '0');
    formData.append('region', editingCity.region || '');
    
    await fetch(`${API}/api/travel-tools/admin/config/cities/${editingCity.id}`, { 
      method: 'PUT', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('City updated');
    setEditingCity(null);
    loadData();
  };

  const deleteCity = async (cityId) => {
    if (!window.confirm('Delete this city?')) return;
    await fetch(`${API}/api/travel-tools/admin/config/cities/${cityId}`, {
      method: 'DELETE',
      headers: authHeaders
    });
    showMessage('City deleted');
    loadData();
  };

  // One-click seed: adds any missing default Moroccan city (with lat/lng).
  // Safe to run repeatedly — server compares by lowercase name and skips duplicates.
  const seedDefaultCities = async () => {
    if (!window.confirm('Add all missing default Moroccan cities to the map?\n\nThis only ADDS cities you don\'t already have. It will not modify or remove anything. Safe to click more than once.')) return;
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/config/cities/seed-defaults`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const namesPreview = (data.added_names || []).slice(0, 8).join(', ');
      const more = (data.added_names || []).length > 8 ? `, +${data.added_names.length - 8} more` : '';
      showMessage(`✓ Added ${data.added} cities · skipped ${data.skipped} duplicates${data.added ? ` — ${namesPreview}${more}` : ''}`);
      loadData();
    } catch (e) {
      showMessage(`Seed failed: ${e.message || e}`);
    }
  };

  // ========== MARKER EMOJIS CRUD ==========
  const addEmoji = async () => {
    if (!newEmoji.id || (!newEmoji.emoji && !newEmoji.image_url)) {
      showMessage('ID required, plus an emoji or image URL');
      return;
    }
    const formData = new FormData();
    formData.append('id', newEmoji.id);
    formData.append('emoji', newEmoji.emoji || '');
    formData.append('name', newEmoji.name || '');
    formData.append('image_url', newEmoji.image_url || '');

    const res = await fetch(`${API}/api/travel-tools/admin/config/emojis`, {
      method: 'POST',
      body: formData,
      headers: authHeaders
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      showMessage(`Add failed: ${err.detail || res.statusText}`);
      return;
    }
    showMessage('Emoji added');
    setNewEmoji({ id: '', emoji: '', name: '', image_url: '' });
    loadData();
  };

  const updateEmoji = async () => {
    if (!editingEmoji?.id) return;
    const originalId = editingEmoji._originalId || editingEmoji.id;
    const formData = new FormData();
    if (editingEmoji.emoji !== undefined) formData.append('emoji', editingEmoji.emoji || '');
    if (editingEmoji.name !== undefined) formData.append('name', editingEmoji.name || '');
    if (editingEmoji.image_url !== undefined) formData.append('image_url', editingEmoji.image_url || '');
    if (editingEmoji.id && editingEmoji.id !== originalId) formData.append('new_id', editingEmoji.id);
    const res = await fetch(`${API}/api/travel-tools/admin/config/emojis/${originalId}`, {
      method: 'PUT',
      body: formData,
      headers: authHeaders
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      showMessage(`Update failed: ${err.detail || res.statusText}`);
      return;
    }
    showMessage('Emoji updated');
    setEditingEmoji(null);
    loadData();
  };

  const deleteEmoji = async (emojiId) => {
    if (!window.confirm('Delete this emoji?')) return;
    await fetch(`${API}/api/travel-tools/admin/config/emojis/${emojiId}`, {
      method: 'DELETE',
      headers: authHeaders
    });
    showMessage('Emoji deleted');
    loadData();
  };

  // Reorder via drag-drop: caller passes the NEW emojis list (already locally reordered).
  // We optimistically update local UI then sync to backend; on failure we reload from server.
  const reorderEmojis = async (newList) => {
    setEmojis(newList);
    const idsCsv = newList.map(e => e.id).join(',');
    const formData = new FormData();
    formData.append('ids', idsCsv);
    const res = await fetch(`${API}/api/travel-tools/admin/config/emojis/reorder`, {
      method: 'PUT',
      body: formData,
      headers: authHeaders
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      showMessage(`Reorder failed: ${err.detail || res.statusText}`);
      loadData();
      return;
    }
    showMessage('Order saved');
  };

  const handleEmojiDragStart = (id) => () => setDragId(id);
  const handleEmojiDragOver = (e) => { e.preventDefault(); };
  const handleEmojiDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const fromIdx = emojis.findIndex(x => x.id === dragId);
    const toIdx = emojis.findIndex(x => x.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return; }
    const next = [...emojis];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setDragId(null);
    reorderEmojis(next);
  };

  // ========== SHOPPING UNITS CRUD ==========
  const addUnit = async () => {
    if (!newUnit.id || !newUnit.name) { showMessage('ID and Name required'); return; }
    const formData = new FormData();
    formData.append('id', newUnit.id);
    formData.append('name', newUnit.name);
    formData.append('symbol', newUnit.symbol);
    
    await fetch(`${API}/api/travel-tools/admin/config/units`, { 
      method: 'POST', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('Unit added');
    setNewUnit({ id: '', name: '', symbol: '' });
    loadData();
  };

  const deleteUnit = async (unitId) => {
    if (!window.confirm('Delete this unit?')) return;
    await fetch(`${API}/api/travel-tools/admin/config/units/${unitId}`, { 
      method: 'DELETE',
      headers: authHeaders 
    });
    showMessage('Unit deleted');
    loadData();
  };

  // ========== SHOPPING CATEGORIES CRUD ==========
  const addShoppingCategory = async () => {
    if (!newShoppingCategory.id || !newShoppingCategory.name) { showMessage('ID and Name required'); return; }
    const formData = new FormData();
    formData.append('id', newShoppingCategory.id);
    formData.append('name', newShoppingCategory.name);
    formData.append('icon', newShoppingCategory.icon);
    
    await fetch(`${API}/api/travel-tools/admin/config/shopping-categories`, { 
      method: 'POST', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('Category added');
    setNewShoppingCategory({ id: '', name: '', icon: '📦' });
    loadData();
  };

  const deleteShoppingCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    await fetch(`${API}/api/travel-tools/admin/config/shopping-categories/${catId}`, { 
      method: 'DELETE',
      headers: authHeaders 
    });
    showMessage('Category deleted');
    loadData();
  };

  // ========== BUDGET CATEGORIES CRUD ==========
  const addBudgetCategory = async () => {
    if (!newBudgetCategory.id || !newBudgetCategory.name) { showMessage('ID and Name required'); return; }
    const formData = new FormData();
    formData.append('id', newBudgetCategory.id);
    formData.append('name', newBudgetCategory.name);
    formData.append('icon', newBudgetCategory.icon);
    
    await fetch(`${API}/api/travel-tools/admin/config/budget-categories`, { 
      method: 'POST', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('Category added');
    setNewBudgetCategory({ id: '', name: '', icon: '💰' });
    loadData();
  };

  const deleteBudgetCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    await fetch(`${API}/api/travel-tools/admin/config/budget-categories/${catId}`, { 
      method: 'DELETE',
      headers: authHeaders 
    });
    showMessage('Category deleted');
    loadData();
  };

  // ========== PHRASE CATEGORIES CRUD ==========
  const addPhraseCategory = async () => {
    if (!newPhraseCategory.id || !newPhraseCategory.name) { showMessage('ID and Name required'); return; }
    const formData = new FormData();
    formData.append('id', newPhraseCategory.id);
    formData.append('name', newPhraseCategory.name);
    formData.append('icon', newPhraseCategory.icon);
    formData.append('image_url', newPhraseCategory.image_url || '');

    const res = await fetch(`${API}/api/travel-tools/admin/config/phrase-categories`, {
      method: 'POST',
      body: formData,
      headers: authHeaders
    });
    const data = await res.json();
    if (data.error) {
      showMessage(data.message);
    } else {
      showMessage('Category added');
      setNewPhraseCategory({ id: '', name: '', icon: '📝', image_url: '' });
      loadData();
    }
  };

  const updatePhraseCategory = async () => {
    if (!editingPhraseCategory?.id) return;
    const formData = new FormData();
    if (editingPhraseCategory.name !== undefined) formData.append('name', editingPhraseCategory.name || '');
    if (editingPhraseCategory.icon !== undefined) formData.append('icon', editingPhraseCategory.icon || '');
    if (editingPhraseCategory.image_url !== undefined) formData.append('image_url', editingPhraseCategory.image_url || '');
    await fetch(`${API}/api/travel-tools/admin/config/phrase-categories/${editingPhraseCategory.id}`, {
      method: 'PUT',
      body: formData,
      headers: authHeaders
    });
    showMessage('Category updated');
    setEditingPhraseCategory(null);
    loadData();
  };

  const deletePhraseCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    await fetch(`${API}/api/travel-tools/admin/config/phrase-categories/${catId}`, {
      method: 'DELETE',
      headers: authHeaders
    });
    showMessage('Category deleted');
    loadData();
  };

  // ========== PHRASE FIELDS MANAGEMENT ==========
  
  const updatePhrasesTitle = async () => {
    const formData = new FormData();
    formData.append('title', phrasesTitle);
    
    await fetch(`${API}/api/travel-tools/admin/config/phrases-title`, { 
      method: 'PUT', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('Title updated!');
  };

  const updateShoppingTitle = async () => {
    const formData = new FormData();
    formData.append('title', shoppingTitle);
    
    await fetch(`${API}/api/travel-tools/admin/config/shopping-title`, { 
      method: 'PUT', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('Shopping title updated!');
  };

  const updateBudgetTitle = async () => {
    const formData = new FormData();
    formData.append('title', budgetTitle);
    
    await fetch(`${API}/api/travel-tools/admin/config/budget-title`, { 
      method: 'PUT', 
      body: formData,
      headers: authHeaders 
    });
    showMessage('Budget title updated!');
  };

  const addPhraseField = async () => {
    if (!newPhraseField.id || !newPhraseField.name) {
      showMessage('ID and Name required');
      return;
    }
    const formData = new FormData();
    formData.append('id', newPhraseField.id);
    formData.append('name', newPhraseField.name);
    formData.append('icon', newPhraseField.icon);
    
    const res = await fetch(`${API}/api/travel-tools/admin/config/phrase-fields`, { 
      method: 'POST', 
      body: formData,
      headers: authHeaders 
    });
    const data = await res.json();
    if (data.error) {
      showMessage(data.message);
    } else {
      showMessage('Field added!');
      setNewPhraseField({ id: '', name: '', icon: '📝' });
      loadData();
    }
  };

  const savePhraseFieldName = async (fieldId, newName) => {
    if (!newName.trim()) return;
    const formData = new FormData();
    formData.append('name', newName.trim());
    await fetch(`${API}/api/travel-tools/admin/config/phrase-fields/${fieldId}`, {
      method: 'PUT',
      body: formData,
      headers: authHeaders
    });
    showMessage('Field name updated!');
    setEditingFieldId(null);
    loadData();
  };

  const savePhraseFieldIcon = async (fieldId, newIcon) => {
    if (!newIcon.trim()) return;
    const formData = new FormData();
    formData.append('icon', newIcon.trim());
    await fetch(`${API}/api/travel-tools/admin/config/phrase-fields/${fieldId}`, {
      method: 'PUT',
      body: formData,
      headers: authHeaders
    });
    showMessage('Field icon updated!');
    setEditingFieldIconId(null);
    loadData();
  };

  const deletePhraseField = async (fieldId) => {
    const res = await fetch(`${API}/api/travel-tools/admin/config/phrase-fields/${fieldId}`, { 
      method: 'DELETE',
      headers: authHeaders 
    });
    const data = await res.json();
    if (data.error) {
      showMessage(data.message);
    } else {
      showMessage('Field deleted');
      loadData();
    }
  };

  // Drag & Drop handlers for reordering
  const handleDragStart = (e, field) => {
    setDraggedField(field);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetField) => {
    e.preventDefault();
    if (!draggedField || draggedField.id === targetField.id) return;

    const newFields = [...phraseFields];
    const draggedIdx = newFields.findIndex(f => f.id === draggedField.id);

    // Remove dragged item first
    const [draggedItem] = newFields.splice(draggedIdx, 1);
    // Find target's new index after removal (avoids off-by-one when dragging down)
    const newTargetIdx = newFields.findIndex(f => f.id === targetField.id);
    newFields.splice(newTargetIdx, 0, draggedItem);

    setPhraseFields(newFields);
    setDraggedField(null);
    setFieldsOrderChanged(true);
  };

  const saveFieldOrder = async () => {
    setSavingFieldOrder(true);
    try {
      const fieldIds = phraseFields.map(f => f.id).join(',');
      const formData = new FormData();
      formData.append('field_ids', fieldIds);
      const res = await fetch(`${API}/api/travel-tools/admin/config/phrase-fields/reorder`, {
        method: 'PUT',
        body: formData,
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      if (res.ok && data.fields) {
        // Verify the returned order matches what we sent
        const returnedIds = data.fields.map(f => f.id).join(',');
        if (returnedIds === fieldIds) {
          showMessage('Field order saved to database!');
        } else {
          showMessage('Order saved (verify by refreshing)');
        }
        setFieldsOrderChanged(false);
        loadData();
      } else if (res.ok && data.message === 'Field updated') {
        // Wrong endpoint hit - backend needs restart
        showMessage('ERROR: Backend needs restart! Route conflict detected.');
      } else {
        showMessage('Failed to save: ' + (data.detail || data.message || res.status));
      }
    } catch (err) {
      showMessage('Error saving order: ' + err.message);
    } finally {
      setSavingFieldOrder(false);
    }
  };

  // Tab configuration - reorganized
  const tabs = [
    { id: 'stats', label: '📊 Stats', color: 'blue' },
    { id: 'cities', label: '🏙️ Cities', color: 'cyan' },
    { id: 'markers', label: '📍 Markers', color: 'green' },
    { id: 'shopping', label: '🛒 Shopping', color: 'orange' },
    { id: 'budget', label: '💰 Budget', color: 'yellow' },
    { id: 'fields', label: '📝 Phrases', color: 'indigo' },
    { id: 'disclaimer', label: '⚠️ Disclaimer', color: 'amber' },
    { id: 'guide', label: '❓ How It Works', color: 'teal' },
    { id: 'guide_cards', label: '📖 Guide Cards', color: 'cyan' },
    { id: 'flagged', label: '🚩 Flagged', color: 'rose' },
    { id: 'limits', label: '🔧 Limits', color: 'purple' },
    { id: 'blocked', label: '🚫 Blocked', color: 'red' },
    { id: 'widgets', label: '🧩 Widgets', color: 'pink' },
    { id: 'distance', label: '📏 Distance', color: 'indigo' },
    { id: 'config', label: '⚙️ Config', color: 'gray' }
  ];

  return (
    <div className="p-6 bg-gray-900 min-h-screen text-white">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">🇲🇦 Travel Tools Admin</h1>
        
        {/* Visibility Controls */}
        <div className="flex gap-3">
          <button
            onClick={toggleVisibility}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              visibility.is_visible
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {visibility.is_visible ? '👁️ Visible' : '👁️‍🗨️ Hidden'}
          </button>
          <button
            onClick={toggleProOnly}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              visibility.is_pro_only
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                : 'bg-gray-600 hover:bg-gray-700 text-white'
            }`}
          >
            {visibility.is_pro_only ? '⭐ Pro Only' : '🌍 All Users'}
          </button>
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-pulse">
          {message}
        </div>
      )}

      {/* Tab Navigation — wraps to multiple rows so every tab stays visible (was overflow-x-auto, hid "Config" off-screen). */}
      <div className="mb-6 pb-2">
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? `bg-${tab.color}-600 text-white shadow-lg scale-105`
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <>
          {/* ============ STATS TAB ============ */}
          {activeTab === 'stats' && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Users" value={stats.total_users} icon="👥" color="blue" />
                <StatCard label="Total Markers" value={stats.total_markers} icon="📍" color="green" />
                <StatCard label="Total Phrases" value={stats.total_phrases} icon="📝" color="purple" />
                <StatCard label="Shopping Lists" value={stats.total_shopping_lists} icon="🛒" color="orange" />
                <StatCard label="Budget Entries" value={stats.total_budget_entries} icon="💰" color="yellow" />
                <StatCard label="Wishlists" value={stats.total_wishlists} icon="⭐" color="pink" />
                <StatCard label="Flagged Users" value={stats.flagged_users_count} icon="🚩" color="amber" />
                <StatCard label="Blocked Users" value={stats.blocked_users_count} icon="🚫" color="red" />
              </div>
              
              {stats.phrases_by_category && Object.keys(stats.phrases_by_category).length > 0 && (
                <div className="bg-gray-800 rounded-xl p-4">
                  <h3 className="font-bold mb-3">Phrases by Category</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(stats.phrases_by_category).map(([cat, count]) => (
                      <div key={cat} className="bg-gray-700 rounded p-2 flex justify-between">
                        <span className="capitalize">{cat}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============ CITIES TAB ============ */}
          {activeTab === 'cities' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 rounded-xl p-6 border border-cyan-700/50">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">🏙️</span>
                    <div>
                      <h2 className="text-2xl font-bold">Cities Management</h2>
                      <p className="text-gray-400 text-sm">Manage Moroccan cities for the map feature</p>
                    </div>
                  </div>
                  <button
                    onClick={seedDefaultCities}
                    className="shrink-0 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold shadow-lg"
                    title="One-click: adds any missing default Moroccan city (with lat/lng). Skips duplicates. Won't modify existing cities."
                  >
                    📥 Add missing default cities
                  </button>
                </div>
                <p className="text-cyan-300/70 text-xs mt-3">
                  Tip: click <b>Add missing default cities</b> to instantly populate the map with ~55 well-known Moroccan cities (lat/lng included). Only adds ones you don't already have.
                </p>
              </div>

              {/* Add City Form */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>➕</span> Add New City
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">City Name (English) *</label>
                    <input
                      placeholder="e.g., Casablanca"
                      value={newCity.name}
                      onChange={(e) => setNewCity({ ...newCity, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Darija (Latin)</label>
                    <input
                      placeholder="e.g., Dar Lbida"
                      value={newCity.name_darija}
                      onChange={(e) => setNewCity({ ...newCity, name_darija: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Arabic Script</label>
                    <input
                      placeholder="الدار البيضاء"
                      value={newCity.name_ar}
                      onChange={(e) => setNewCity({ ...newCity, name_ar: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none text-right"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Latitude *</label>
                    <input
                      placeholder="33.5731"
                      type="number"
                      step="any"
                      value={newCity.lat}
                      onChange={(e) => setNewCity({ ...newCity, lat: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Longitude *</label>
                    <input
                      placeholder="-7.5898"
                      type="number"
                      step="any"
                      value={newCity.lng}
                      onChange={(e) => setNewCity({ ...newCity, lng: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Region</label>
                    <input
                      placeholder="e.g., Central, North, South"
                      value={newCity.region}
                      onChange={(e) => setNewCity({ ...newCity, region: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={addCity}
                  className="mt-4 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 rounded-lg font-medium transition-colors"
                >
                  ➕ Add City
                </button>
              </div>

              {/* Cities List */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">📋 Cities ({cities.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 rounded-tl-lg">Name</th>
                        <th className="px-4 py-3">Darija</th>
                        <th className="px-4 py-3">Arabic</th>
                        <th className="px-4 py-3">Coordinates</th>
                        <th className="px-4 py-3">Region</th>
                        <th className="px-4 py-3 rounded-tr-lg">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cities.map(city => (
                        <tr key={city.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="px-4 py-3 font-medium">{city.name}</td>
                          <td className="px-4 py-3 text-gray-400">{city.name_darija || '-'}</td>
                          <td className="px-4 py-3 text-gray-400" dir="rtl">{city.name_ar || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">{city.lat}, {city.lng}</td>
                          <td className="px-4 py-3 text-gray-400">{city.region || '-'}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setEditingCity(city)}
                              className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/30 rounded-lg transition-colors mr-1"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => deleteCity(city.id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {cities.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No cities added yet</p>
                )}
              </div>

              {/* Edit City Modal */}
              {editingCity && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                  <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full mx-4 border border-cyan-600">
                    <h3 className="text-xl font-bold text-cyan-400 mb-4">✏️ Edit City: {editingCity.name}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">City Name (English)</label>
                        <input
                          value={editingCity.name}
                          onChange={(e) => setEditingCity({ ...editingCity, name: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Darija (Latin)</label>
                        <input
                          value={editingCity.name_darija || ''}
                          onChange={(e) => setEditingCity({ ...editingCity, name_darija: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Arabic Script</label>
                        <input
                          value={editingCity.name_ar || ''}
                          onChange={(e) => setEditingCity({ ...editingCity, name_ar: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 text-right"
                          dir="rtl"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Region</label>
                        <input
                          value={editingCity.region || ''}
                          onChange={(e) => setEditingCity({ ...editingCity, region: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Latitude</label>
                        <input
                          type="number"
                          step="any"
                          value={editingCity.lat}
                          onChange={(e) => setEditingCity({ ...editingCity, lat: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Longitude</label>
                        <input
                          type="number"
                          step="any"
                          value={editingCity.lng}
                          onChange={(e) => setEditingCity({ ...editingCity, lng: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 justify-end mt-6">
                      <button
                        onClick={() => setEditingCity(null)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={updateCity}
                        className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg font-medium"
                      >
                        💾 Save Changes
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============ MARKERS TAB ============ */}
          {activeTab === 'markers' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 rounded-xl p-6 border border-green-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">📍</span>
                  <div>
                    <h2 className="text-2xl font-bold">Marker Emojis</h2>
                    <p className="text-gray-400 text-sm">Emojis users can use for map markers</p>
                  </div>
                </div>
              </div>

              {/* Add Emoji Form */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>➕</span> Add New Marker Emoji
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">ID *</label>
                    <input
                      placeholder="restaurant"
                      value={newEmoji.id}
                      onChange={(e) => setNewEmoji({ ...newEmoji, id: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Emoji {!newEmoji.image_url && '*'}</label>
                    <input
                      placeholder="🍽️"
                      value={newEmoji.emoji}
                      onChange={(e) => setNewEmoji({ ...newEmoji, emoji: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none text-center text-2xl"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Name</label>
                    <input
                      placeholder="Restaurant"
                      value={newEmoji.name}
                      onChange={(e) => setNewEmoji({ ...newEmoji, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none"
                    />
                  </div>
                </div>
                {/* Image URL row — overrides emoji on the user-facing side when set */}
                <div className="mt-3">
                  <label className="block text-sm text-gray-400 mb-1">Image URL <span className="text-gray-500">(optional — overrides emoji when set)</span></label>
                  <div className="flex gap-2">
                    <input
                      placeholder="https://… or pick from Image Manager"
                      value={newEmoji.image_url}
                      onChange={(e) => setNewEmoji({ ...newEmoji, image_url: e.target.value })}
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => { setImagePickerTarget({ kind: 'newEmoji' }); setImagePickerOpen(true); }}
                      className="shrink-0 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
                    >
                      📁 Pick from Image Manager
                    </button>
                    {newEmoji.image_url && (
                      <button
                        type="button"
                        onClick={() => setNewEmoji({ ...newEmoji, image_url: '' })}
                        className="shrink-0 px-3 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm"
                        title="Clear image URL"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {newEmoji.image_url && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                      <span>Preview:</span>
                      <img src={newEmoji.image_url} alt="" className="w-8 h-8 rounded bg-gray-700 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
                <button
                  onClick={addEmoji}
                  className="mt-4 px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors"
                >
                  ➕ Add Emoji
                </button>
              </div>

              {/* Emojis Grid */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">🎨 Available Emojis ({emojis.length})</h3>
                <p className="text-xs text-gray-500 mb-3">Click any row to edit · Set an Image URL to replace the emoji with a real icon image (saved to DB).</p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {emojis.map(emoji => {
                    const isEditing = (editingEmoji?._originalId || editingEmoji?.id) === emoji.id;
                    if (isEditing) {
                      return (
                        <div key={emoji.id} className="bg-gray-900 border-2 border-blue-500 rounded-lg p-3 col-span-2 md:col-span-4 lg:col-span-6">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-blue-400">Editing</span>
                            <span className="text-xs text-gray-500">(was: {editingEmoji._originalId})</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">ID</label>
                              <input
                                value={editingEmoji.id || ''}
                                onChange={(e) => setEditingEmoji({ ...editingEmoji, id: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">Emoji</label>
                              <input
                                value={editingEmoji.emoji || ''}
                                onChange={(e) => setEditingEmoji({ ...editingEmoji, emoji: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-center text-xl"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">Name</label>
                              <input
                                value={editingEmoji.name || ''}
                                onChange={(e) => setEditingEmoji({ ...editingEmoji, name: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-xs text-gray-400 mb-1">Image URL <span className="text-gray-500">(replaces emoji when set)</span></label>
                            <div className="flex gap-2">
                              <input
                                placeholder="https://… or pick from Image Manager"
                                value={editingEmoji.image_url || ''}
                                onChange={(e) => setEditingEmoji({ ...editingEmoji, image_url: e.target.value })}
                                className="flex-1 min-w-0 px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => { setImagePickerTarget({ kind: 'editEmoji', id: emoji.id }); setImagePickerOpen(true); }}
                                className="shrink-0 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium whitespace-nowrap"
                              >
                                📁 Pick
                              </button>
                              {editingEmoji.image_url && (
                                <button
                                  type="button"
                                  onClick={() => setEditingEmoji({ ...editingEmoji, image_url: '' })}
                                  className="shrink-0 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm"
                                  title="Clear image URL"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                            {editingEmoji.image_url && (
                              <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                                <span>Preview:</span>
                                <img src={editingEmoji.image_url} alt="" className="w-10 h-10 rounded bg-gray-700 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex gap-2">
                            <button onClick={updateEmoji} className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium">💾 Save</button>
                            <button onClick={() => setEditingEmoji(null)} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm">Cancel</button>
                            <button onClick={() => { deleteEmoji(emoji.id); setEditingEmoji(null); }} className="px-3 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm">🗑️ Delete</button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={emoji.id}
                        draggable={true}
                        onDragStart={handleEmojiDragStart(emoji.id)}
                        onDragOver={handleEmojiDragOver}
                        onDrop={handleEmojiDrop(emoji.id)}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => setEditingEmoji({ id: emoji.id, _originalId: emoji.id, emoji: emoji.emoji || '', name: emoji.name || '', image_url: emoji.image_url || '' })}
                        className={`relative bg-gray-700 rounded-lg p-4 text-center transition-colors group cursor-move ${dragId === emoji.id ? 'opacity-40 ring-2 ring-blue-400' : 'hover:bg-gray-600'}`}
                        title="Drag to reorder · Click to edit"
                      >
                        <span className="absolute top-1 left-1 text-gray-500 text-xs select-none" title="Drag handle">⋮⋮</span>
                        {emoji.image_url ? (
                          <img src={emoji.image_url} alt={emoji.name || ''} className="w-12 h-12 mx-auto mb-2 rounded object-cover pointer-events-none" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span className="text-4xl block mb-2 pointer-events-none">{emoji.emoji}</span>
                        )}
                        <p className="font-medium text-sm truncate pointer-events-none">{emoji.name}</p>
                        <p className="text-xs text-gray-500 truncate pointer-events-none">({emoji.id})</p>
                        <p className="text-[10px] text-blue-400 mt-1 opacity-0 group-hover:opacity-100 pointer-events-none">Drag · Click to edit</p>
                      </div>
                    );
                  })}
                </div>
                {emojis.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No emojis added yet</p>
                )}
              </div>
            </div>
          )}

          {/* ============ SHOPPING TAB ============ */}
          {activeTab === 'shopping' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-900/50 to-amber-900/50 rounded-xl p-6 border border-orange-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">🛒</span>
                  <div>
                    <h2 className="text-2xl font-bold">Shopping Configuration</h2>
                    <p className="text-gray-400 text-sm">Manage shopping units and categories</p>
                  </div>
                </div>
              </div>

              {/* Shopping Section Title Editor */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <span>📖</span> Shopping Section Title
                </h3>
                <p className="text-sm text-gray-400 mb-3">This title appears above the Shopping tab for users</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shoppingTitle}
                    onChange={(e) => setShoppingTitle(e.target.value)}
                    placeholder="Shopping Lists"
                    className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    onClick={updateShoppingTitle}
                    className="px-6 py-3 bg-orange-600 hover:bg-orange-700 rounded-lg font-medium transition-colors"
                  >
                    Save Title
                  </button>
                </div>
              </div>

              {/* Shopping Units Section */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>⚖️</span> Shopping Units
                </h3>
                <p className="text-sm text-gray-400 mb-4">Units of measurement for shopping items (kg, pieces, liters, etc.)</p>
                
                {/* Add Unit Form */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                  <input
                    placeholder="ID (e.g., kg)"
                    value={newUnit.id}
                    onChange={(e) => setNewUnit({ ...newUnit, id: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    placeholder="Name (e.g., Kilogram)"
                    value={newUnit.name}
                    onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    placeholder="Symbol (e.g., kg)"
                    value={newUnit.symbol}
                    onChange={(e) => setNewUnit({ ...newUnit, symbol: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    onClick={addUnit}
                    className="px-4 py-3 bg-orange-600 hover:bg-orange-700 rounded-lg font-medium transition-colors"
                  >
                    ➕ Add Unit
                  </button>
                </div>
                
                {/* Units List */}
                <div className="flex flex-wrap gap-2">
                  {units.map(unit => (
                    <div key={unit.id} className="bg-gray-700 rounded-lg px-4 py-2 flex items-center gap-3 group hover:bg-gray-600 transition-colors">
                      <span className="font-medium">{unit.name}</span>
                      <span className="text-gray-400">({unit.symbol})</span>
                      <button
                        onClick={() => deleteUnit(unit.id)}
                        className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                {units.length === 0 && (
                  <p className="text-gray-500 py-4">No units added yet</p>
                )}
              </div>

              {/* Shopping Categories Section */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>📦</span> Shopping Categories
                </h3>
                <p className="text-sm text-gray-400 mb-4">Categories for organizing shopping items</p>
                
                {/* Add Category Form */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                  <input
                    placeholder="ID (e.g., groceries)"
                    value={newShoppingCategory.id}
                    onChange={(e) => setNewShoppingCategory({ ...newShoppingCategory, id: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    placeholder="Name (e.g., Groceries)"
                    value={newShoppingCategory.name}
                    onChange={(e) => setNewShoppingCategory({ ...newShoppingCategory, name: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    placeholder="Icon 📦"
                    value={newShoppingCategory.icon}
                    onChange={(e) => setNewShoppingCategory({ ...newShoppingCategory, icon: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none text-center text-xl"
                  />
                  <button
                    onClick={addShoppingCategory}
                    className="px-4 py-3 bg-orange-600 hover:bg-orange-700 rounded-lg font-medium transition-colors"
                  >
                    ➕ Add Category
                  </button>
                </div>
                
                {/* Categories Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {shoppingCategories.map(cat => (
                    <div key={cat.id} className="bg-gray-700 rounded-lg p-4 text-center group hover:bg-gray-600 transition-colors">
                      <span className="text-3xl block mb-2">{cat.icon}</span>
                      <p className="font-medium">{cat.name}</p>
                      <button
                        onClick={() => deleteShoppingCategory(cat.id)}
                        className="mt-2 text-red-400 hover:text-red-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
                {shoppingCategories.length === 0 && (
                  <p className="text-gray-500 py-4">No categories added yet</p>
                )}
              </div>
            </div>
          )}

          {/* ============ BUDGET TAB ============ */}
          {activeTab === 'budget' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-yellow-900/50 to-amber-900/50 rounded-xl p-6 border border-yellow-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">💰</span>
                  <div>
                    <h2 className="text-2xl font-bold">Budget Configuration</h2>
                    <p className="text-gray-400 text-sm">Categories for expense tracking</p>
                  </div>
                </div>
              </div>

              {/* Budget Section Title Editor */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <span>📖</span> Budget Section Title
                </h3>
                <p className="text-sm text-gray-400 mb-3">This title appears above the Budget tab for users</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={budgetTitle}
                    onChange={(e) => setBudgetTitle(e.target.value)}
                    placeholder="Budget Tracker"
                    className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  />
                  <button
                    onClick={updateBudgetTitle}
                    className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 rounded-lg font-medium transition-colors"
                  >
                    Save Title
                  </button>
                </div>
              </div>

              {/* Add Budget Category Form */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>➕</span> Add Budget Category
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">ID *</label>
                    <input
                      placeholder="food"
                      value={newBudgetCategory.id}
                      onChange={(e) => setNewBudgetCategory({ ...newBudgetCategory, id: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Name *</label>
                    <input
                      placeholder="Food & Dining"
                      value={newBudgetCategory.name}
                      onChange={(e) => setNewBudgetCategory({ ...newBudgetCategory, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Icon</label>
                    <input
                      placeholder="🍔"
                      value={newBudgetCategory.icon}
                      onChange={(e) => setNewBudgetCategory({ ...newBudgetCategory, icon: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none text-center text-2xl"
                    />
                  </div>
                </div>
                <button
                  onClick={addBudgetCategory}
                  className="mt-4 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 rounded-lg font-medium transition-colors"
                >
                  ➕ Add Category
                </button>
              </div>

              {/* Budget Categories Grid */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">📋 Categories ({budgetCategories.length})</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {budgetCategories.map(cat => (
                    <div key={cat.id} className="bg-gray-700 rounded-xl p-5 text-center group hover:bg-gray-600 transition-all hover:scale-105">
                      <span className="text-5xl block mb-3">{cat.icon}</span>
                      <p className="font-bold text-lg">{cat.name}</p>
                      <p className="text-xs text-gray-500 mb-3">({cat.id})</p>
                      <button
                        onClick={() => deleteBudgetCategory(cat.id)}
                        className="text-red-400 hover:text-red-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
                {budgetCategories.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No budget categories added yet</p>
                )}
              </div>
            </div>
          )}

          {/* ============ PHRASE FIELDS TAB ============ */}
          {activeTab === 'fields' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-xl p-6 border border-indigo-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">📝</span>
                  <div>
                    <h2 className="text-2xl font-bold">Phrases Configuration</h2>
                    <p className="text-gray-400 text-sm">Manage phrase fields and categories</p>
                  </div>
                </div>
              </div>

              {/* Title Editor */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <span>📖</span> Phrases Section Title
                </h3>
                <p className="text-sm text-gray-400 mb-3">This title appears above the Phrases tab for users</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={phrasesTitle}
                    onChange={(e) => setPhrasesTitle(e.target.value)}
                    placeholder="Make Your Own Dictionary"
                    className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  />
                  <button
                    onClick={updatePhrasesTitle}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium transition-colors"
                  >
                    Save Title
                  </button>
                </div>
              </div>

              {/* Phrase Categories */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span>🏷️</span> Phrase Categories
                </h3>
                <p className="text-sm text-gray-400 mb-4">Categories for organizing phrases</p>
                
                {/* Add Category Form */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                  <input
                    placeholder="ID (e.g., food)"
                    value={newPhraseCategory.id}
                    onChange={(e) => setNewPhraseCategory({ ...newPhraseCategory, id: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  />
                  <input
                    placeholder="Name (e.g., Food)"
                    value={newPhraseCategory.name}
                    onChange={(e) => setNewPhraseCategory({ ...newPhraseCategory, name: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  />
                  <input
                    placeholder="Icon 📝"
                    value={newPhraseCategory.icon}
                    onChange={(e) => setNewPhraseCategory({ ...newPhraseCategory, icon: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-center text-xl"
                  />
                  <button
                    onClick={addPhraseCategory}
                    className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium transition-colors"
                  >
                    ➕ Add Category
                  </button>
                </div>
                {/* Image URL row for new category (optional — overrides icon when set) */}
                <div className="mb-4 flex gap-2">
                  <input
                    placeholder="Image URL (optional — overrides icon)"
                    value={newPhraseCategory.image_url}
                    onChange={(e) => setNewPhraseCategory({ ...newPhraseCategory, image_url: e.target.value })}
                    className="flex-1 min-w-0 px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => { setImagePickerTarget({ kind: 'newCategory' }); setImagePickerOpen(true); }}
                    className="shrink-0 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium whitespace-nowrap"
                  >
                    📁 Pick from Image Manager
                  </button>
                  {newPhraseCategory.image_url && (
                    <button
                      type="button"
                      onClick={() => setNewPhraseCategory({ ...newPhraseCategory, image_url: '' })}
                      className="shrink-0 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm"
                      title="Clear image URL"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Categories Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {phraseCategories.map(cat => (
                    <div key={cat.id} className="bg-gray-700 rounded-lg p-4 text-center group hover:bg-gray-600 transition-colors">
                      {cat.image_url ? (
                        <img src={cat.image_url} alt={cat.name} className="w-10 h-10 mx-auto mb-2 rounded object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <span className="text-3xl block mb-2">{cat.icon}</span>
                      )}
                      <p className="font-medium">{cat.name}</p>
                      <div className="mt-2 flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingPhraseCategory({ ...cat, image_url: cat.image_url || '' })}
                          className="text-yellow-400 hover:text-yellow-300 text-sm"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deletePhraseCategory(cat.id)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {phraseCategories.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No phrase categories added yet</p>
                )}
              </div>

              {/* Edit Phrase Category Modal */}
              {editingPhraseCategory && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                  <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-yellow-600">
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">✏️ Edit Category</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Category Name</label>
                        <input
                          value={editingPhraseCategory.name || ''}
                          onChange={(e) => setEditingPhraseCategory({ ...editingPhraseCategory, name: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Icon</label>
                        <input
                          value={editingPhraseCategory.icon || ''}
                          onChange={(e) => setEditingPhraseCategory({ ...editingPhraseCategory, icon: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 text-2xl"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Image URL <span className="text-gray-500">(optional — overrides icon)</span></label>
                        <div className="flex gap-2">
                          <input
                            placeholder="https://… or pick from Image Manager"
                            value={editingPhraseCategory.image_url || ''}
                            onChange={(e) => setEditingPhraseCategory({ ...editingPhraseCategory, image_url: e.target.value })}
                            className="flex-1 min-w-0 px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => { setImagePickerTarget({ kind: 'editCategory', id: editingPhraseCategory.id }); setImagePickerOpen(true); }}
                            className="shrink-0 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium whitespace-nowrap"
                          >
                            📁 Pick
                          </button>
                          {editingPhraseCategory.image_url && (
                            <button
                              type="button"
                              onClick={() => setEditingPhraseCategory({ ...editingPhraseCategory, image_url: '' })}
                              className="shrink-0 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm"
                              title="Clear"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        {editingPhraseCategory.image_url && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                            <span>Preview:</span>
                            <img src={editingPhraseCategory.image_url} alt="" className="w-10 h-10 rounded bg-gray-700 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-3 justify-end mt-6">
                      <button
                        onClick={() => setEditingPhraseCategory(null)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={updatePhraseCategory}
                        className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg font-medium"
                      >
                        💾 Save
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Phrase Fields - Drag & Drop */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <span>📋</span> Phrase Text Fields
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                  Drag to reorder fields. Users will see these fields when adding phrases.
                  <br />Default fields (English, Darija, Other) cannot be deleted.
                </p>
                
                <div className="space-y-2 mb-4">
                  {phraseFields.map((field, idx) => (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, field)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, field)}
                      className={`flex items-center justify-between p-4 rounded-lg cursor-move transition-all ${
                        draggedField?.id === field.id 
                          ? 'bg-indigo-900 opacity-50' 
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-gray-400 cursor-grab text-xl">⠿</span>
                        {editingFieldIconId === field.id ? (
                          <input
                            autoFocus
                            value={editingFieldIcon}
                            onChange={(e) => setEditingFieldIcon(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePhraseFieldIcon(field.id, editingFieldIcon);
                              if (e.key === 'Escape') setEditingFieldIconId(null);
                            }}
                            onBlur={() => savePhraseFieldIcon(field.id, editingFieldIcon)}
                            className="bg-gray-600 text-white text-2xl text-center w-12 h-10 rounded border border-indigo-500 focus:outline-none"
                          />
                        ) : (
                          <span
                            className="text-2xl cursor-pointer hover:scale-125 transition-transform"
                            onClick={(e) => { e.stopPropagation(); setEditingFieldIconId(field.id); setEditingFieldIcon(field.icon); }}
                            title="Click to edit icon"
                          >
                            {field.icon}
                          </span>
                        )}
                        {editingFieldId === field.id ? (
                          <input
                            autoFocus
                            value={editingFieldName}
                            onChange={(e) => setEditingFieldName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePhraseFieldName(field.id, editingFieldName);
                              if (e.key === 'Escape') setEditingFieldId(null);
                            }}
                            onBlur={() => savePhraseFieldName(field.id, editingFieldName)}
                            className="bg-gray-600 text-white px-2 py-1 rounded text-lg font-medium border border-indigo-500 focus:outline-none focus:border-indigo-400 w-40"
                          />
                        ) : (
                          <span
                            className="font-medium text-lg cursor-pointer hover:text-indigo-400 transition-colors"
                            onClick={(e) => { e.stopPropagation(); setEditingFieldId(field.id); setEditingFieldName(field.name); }}
                            title="Click to edit name"
                          >
                            {field.name}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">({field.id})</span>
                        {field.is_default && (
                          <span className="text-xs bg-indigo-600 px-2 py-0.5 rounded">Default</span>
                        )}
                      </div>
                      {!field.is_default && (
                        <button
                          onClick={() => deletePhraseField(field.id)}
                          className="text-red-400 hover:text-red-300 px-3 py-1"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Save Order Button */}
                {fieldsOrderChanged && (
                  <div className="flex items-center gap-3 mt-3 p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg">
                    <span className="text-yellow-400 text-sm">Unsaved order changes</span>
                    <button
                      onClick={saveFieldOrder}
                      disabled={savingFieldOrder}
                      className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 transition-all"
                    >
                      {savingFieldOrder ? 'Saving...' : 'Save Order'}
                    </button>
                  </div>
                )}

                {/* Add new field */}
                <div className="border-t border-gray-600 pt-4">
                  <h4 className="font-medium mb-3">Add Custom Field</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      placeholder="ID (e.g., pronunciation)"
                      value={newPhraseField.id}
                      onChange={(e) => setNewPhraseField({ ...newPhraseField, id: e.target.value })}
                      className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                    <input
                      placeholder="Display Name"
                      value={newPhraseField.name}
                      onChange={(e) => setNewPhraseField({ ...newPhraseField, name: e.target.value })}
                      className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    />
                    <input
                      placeholder="Icon 📝"
                      value={newPhraseField.icon}
                      onChange={(e) => setNewPhraseField({ ...newPhraseField, icon: e.target.value })}
                      className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-center text-xl"
                    />
                    <button
                      onClick={addPhraseField}
                      className="px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors"
                    >
                      ➕ Add Field
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <span>👁️</span> Preview (How users will see it)
                </h3>
                <div className="bg-gray-900 rounded-lg p-6">
                  <h4 className="text-xl font-bold text-indigo-400 mb-4">{phrasesTitle}</h4>
                  <div className="space-y-3">
                    {phraseFields.map(field => (
                      <div key={field.id} className="flex items-center gap-3">
                        <span className="w-8 text-xl">{field.icon}</span>
                        <span className="text-gray-400 w-32">{field.name}:</span>
                        <span className="text-white">Example text here...</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ LIMITS TAB ============ */}
          {activeTab === 'limits' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-xl p-6 border border-purple-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">🔧</span>
                  <div>
                    <h2 className="text-2xl font-bold">User Limits</h2>
                    <p className="text-gray-400 text-sm">Set maximum limits for user content. When users hit limits, they'll see an error message.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-6 max-w-2xl">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="w-56 font-medium">📍 Max Markers Per User</label>
                    <input
                      type="number"
                      value={userLimits.max_markers_per_user}
                      onChange={(e) => setUserLimits({ ...userLimits, max_markers_per_user: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-56 font-medium">📝 Max Phrases Per User</label>
                    <input
                      type="number"
                      value={userLimits.max_phrases_per_user}
                      onChange={(e) => setUserLimits({ ...userLimits, max_phrases_per_user: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-56 font-medium">🛒 Max Lists Per User</label>
                    <input
                      type="number"
                      value={userLimits.max_lists_per_user}
                      onChange={(e) => setUserLimits({ ...userLimits, max_lists_per_user: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-56 font-medium">📦 Max Items Per List</label>
                    <input
                      type="number"
                      value={userLimits.max_items_per_list}
                      onChange={(e) => setUserLimits({ ...userLimits, max_items_per_list: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-56 font-medium">⭐ Max Wishlist Per User</label>
                    <input
                      type="number"
                      value={userLimits.max_wishlist_per_user}
                      onChange={(e) => setUserLimits({ ...userLimits, max_wishlist_per_user: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="w-56 font-medium">💰 Max Budget Items Per User</label>
                    <input
                      type="number"
                      value={userLimits.max_budget_items_per_user}
                      onChange={(e) => setUserLimits({ ...userLimits, max_budget_items_per_user: parseInt(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-4 pt-3 border-t border-gray-700">
                    <label className="w-56 font-medium">🔤 Max Characters Per Phrase Field</label>
                    <input
                      type="number"
                      min={10}
                      max={2000}
                      value={userLimits.max_phrase_chars}
                      onChange={(e) => setUserLimits({ ...userLimits, max_phrase_chars: parseInt(e.target.value) || 300 })}
                      className="flex-1 px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-gray-400 -mt-3 ml-60">Caps each phrase field input (English / Darija / Other / custom). Clamped to 10–2000.</p>
                  <button
                    onClick={updateLimits}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors mt-4"
                  >
                    💾 Save Limits
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ============ DISCLAIMER TAB ============ */}
          {activeTab === 'disclaimer' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-amber-900/50 to-yellow-900/50 rounded-xl p-6 border border-amber-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">⚠️</span>
                  <div>
                    <h2 className="text-2xl font-bold">Disclaimer Settings</h2>
                    <p className="text-gray-400 text-sm">Edit the disclaimer text shown to users in My Morocco</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">📝 Disclaimer Text</h3>
                <p className="text-gray-400 text-sm mb-4">
                  This text appears at the bottom of the My Morocco page on both mobile and desktop. 
                  Use it to inform users about privacy, data handling, or any important notices.
                </p>
                <textarea
                  value={disclaimerText}
                  onChange={(e) => setDisclaimerText(e.target.value)}
                  placeholder="Enter your disclaimer text here..."
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none resize-none"
                />
                <div className="flex items-center justify-between mt-4">
                  <span className="text-gray-500 text-sm">{disclaimerText.length} characters</span>
                  <button
                    onClick={async () => {
                      const formData = new FormData();
                      formData.append('disclaimer_text', disclaimerText);
                      const res = await fetch(`${API}/api/travel-tools/admin/config/disclaimer`, {
                        method: 'PUT',
                        body: formData,
                        headers: authHeaders
                      });
                      if (res.ok) {
                        showMessage('Disclaimer saved successfully');
                      } else {
                        showMessage('Failed to save disclaimer');
                      }
                    }}
                    className="px-6 py-3 bg-amber-600 hover:bg-amber-700 rounded-lg font-medium transition-colors"
                  >
                    💾 Save Disclaimer
                  </button>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">👁️ Preview</h3>
                <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
                  <div className="flex items-start gap-2 text-yellow-200">
                    <span className="text-lg flex-shrink-0">⚠️</span>
                    <p className="text-sm leading-relaxed">
                      {disclaimerText || "Your disclaimer text will appear here..."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ FLAGGED TAB ============ */}
          {activeTab === 'flagged' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-amber-900/50 to-orange-900/50 rounded-xl p-6 border border-amber-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">🚩</span>
                  <div>
                    <h2 className="text-2xl font-bold">Flagged Users</h2>
                    <p className="text-gray-400 text-sm">Users approaching or exceeding limits</p>
                  </div>
                </div>
              </div>

              {flaggedUsers.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center text-gray-400">
                  <span className="text-6xl block mb-4">✅</span>
                  <p className="text-xl">No flagged users</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {flaggedUsers.map(flag => (
                    <div key={flag.id} className="bg-gray-800 rounded-xl p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-lg">{flag.user_email}</p>
                          <p className="text-sm text-gray-400">
                            {flag.limit_type}: {flag.current_count}/{flag.max_limit} ({flag.percentage}%)
                          </p>
                          <p className="text-xs text-gray-500">{new Date(flag.flagged_at).toLocaleString()}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => resolveFlag(flag.id)}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium"
                          >
                            ✓ Resolve
                          </button>
                          <button
                            onClick={() => deleteFlag(flag.id)}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============ BLOCKED TAB ============ */}
          {activeTab === 'blocked' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-red-900/50 to-rose-900/50 rounded-xl p-6 border border-red-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">🚫</span>
                  <div>
                    <h2 className="text-2xl font-bold">Blocked Users</h2>
                    <p className="text-gray-400 text-sm">Users blocked from My Morocco features</p>
                  </div>
                </div>
              </div>

              {/* Block user form */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">Block a User</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    placeholder="User ID"
                    value={blockUserForm.user_id}
                    onChange={(e) => setBlockUserForm({ ...blockUserForm, user_id: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-red-500 focus:outline-none"
                  />
                  <input
                    placeholder="User Email"
                    value={blockUserForm.user_email}
                    onChange={(e) => setBlockUserForm({ ...blockUserForm, user_email: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-red-500 focus:outline-none"
                  />
                  <input
                    placeholder="Reason (optional)"
                    value={blockUserForm.reason}
                    onChange={(e) => setBlockUserForm({ ...blockUserForm, reason: e.target.value })}
                    className="px-4 py-3 bg-gray-700 rounded-lg border border-gray-600 focus:border-red-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={blockUser}
                  className="mt-4 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
                >
                  🚫 Block User
                </button>
              </div>
              
              {/* Blocked list */}
              {blockedUsers.length === 0 || !blockedUsers.some(u => u.is_blocked) ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center text-gray-400">
                  <span className="text-6xl block mb-4">✅</span>
                  <p className="text-xl">No blocked users</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {blockedUsers.filter(u => u.is_blocked).map(user => (
                    <div key={user.id} className="bg-gray-800 rounded-xl p-5 flex justify-between items-center">
                      <div>
                        <p className="font-medium text-lg">{user.user_email}</p>
                        <p className="text-sm text-gray-400">Reason: {user.reason || 'Not specified'}</p>
                        <p className="text-xs text-gray-500">Blocked: {new Date(user.blocked_at).toLocaleString()}</p>
                      </div>
                      <button
                        onClick={() => unblockUser(user.user_id)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium"
                      >
                        ✓ Unblock
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============ GUIDE TAB ============ */}
          {activeTab === 'guide' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-teal-800 to-teal-700 rounded-xl p-6 border border-teal-600">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">📖</span>
                  <div>
                    <h2 className="text-2xl font-bold">How It Works (? help modal)</h2>
                    <p className="text-gray-300 text-sm">Manages the content shown when users tap the <strong>?</strong> button on the Travel Tools page.</p>
                  </div>
                </div>
              </div>

              {/* Guide Title & Subtitle */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>✏️</span> Guide Header
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Title</label>
                    <input
                      type="text"
                      value={guideTitle}
                      onChange={(e) => setGuideTitle(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white"
                      placeholder="My Morocco Guide"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={guideSubtitle}
                      onChange={(e) => setGuideSubtitle(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white"
                      placeholder="Your personal travel companion"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-1">Introduction Text</label>
                  <textarea
                    value={guideIntro}
                    onChange={(e) => setGuideIntro(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white h-24 resize-none"
                    placeholder="My Morocco is your all-in-one travel companion..."
                  />
                </div>
                <button
                  onClick={async () => {
                    try {
                      const formData = new FormData();
                      formData.append('title', guideTitle);
                      formData.append('subtitle', guideSubtitle);
                      formData.append('intro', guideIntro);
                      const res = await fetch(`${API}/api/travel-tools/admin/guide/header`, {
                        method: 'PUT',
                        headers: authHeaders,
                        body: formData
                      });
                      if (res.ok) showMessage('Guide header updated!');
                    } catch (error) {
                      showMessage('Failed to update header');
                    }
                  }}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 rounded-lg font-medium"
                >
                  💾 Save Header
                </button>
              </div>

              {/* Add New Guide Item */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>➕</span> Add Guide Section
                </h3>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newGuideItem.icon}
                      onChange={(e) => setNewGuideItem({ ...newGuideItem, icon: e.target.value })}
                      className="w-16 px-3 py-2 bg-gray-700 rounded-lg text-white text-center text-xl"
                      placeholder="📍"
                    />
                    <input
                      type="text"
                      value={newGuideItem.title}
                      onChange={(e) => setNewGuideItem({ ...newGuideItem, title: e.target.value })}
                      className="flex-1 px-4 py-2 bg-gray-700 rounded-lg text-white"
                      placeholder="Section title (e.g., Language Map)"
                    />
                  </div>
                  <textarea
                    value={newGuideItem.content}
                    onChange={(e) => setNewGuideItem({ ...newGuideItem, content: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg text-white h-32 resize-none"
                    placeholder="Section content (detailed description)..."
                  />
                  <button
                    onClick={async () => {
                      if (!newGuideItem.title.trim()) return;
                      try {
                        const formData = new FormData();
                        formData.append('title', newGuideItem.title);
                        formData.append('icon', newGuideItem.icon || '📍');
                        formData.append('content', newGuideItem.content || '');
                        const res = await fetch(`${API}/api/travel-tools/admin/guide/items`, {
                          method: 'POST',
                          headers: authHeaders,
                          body: formData
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setGuideItems([...guideItems, data.item]);
                          setNewGuideItem({ title: '', icon: '📍', content: '' });
                          showMessage('Guide section added!');
                        }
                      } catch (error) {
                        showMessage('Failed to add section');
                      }
                    }}
                    disabled={!newGuideItem.title.trim()}
                    className="px-6 py-2 bg-teal-600 hover:bg-teal-700 rounded-lg font-medium disabled:opacity-50"
                  >
                    ➕ Add Section
                  </button>
                </div>
              </div>

              {/* Guide Items List */}
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>📋</span> Guide Sections ({guideItems.length})
                </h3>
                {guideItems.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No guide sections yet. Add some above!</p>
                ) : (
                  <div className="space-y-3">
                    {guideItems.map((item, index) => (
                      <div key={item.id || index} className="bg-gray-700 rounded-lg overflow-hidden">
                        {editingGuideItem?.id === item.id ? (
                          <div className="p-4 space-y-3">
                            <div className="flex gap-3">
                              <input
                                type="text"
                                value={editingGuideItem.icon}
                                onChange={(e) => setEditingGuideItem({ ...editingGuideItem, icon: e.target.value })}
                                className="w-14 px-2 py-2 bg-gray-600 rounded text-center text-xl"
                              />
                              <input
                                type="text"
                                value={editingGuideItem.title}
                                onChange={(e) => setEditingGuideItem({ ...editingGuideItem, title: e.target.value })}
                                className="flex-1 px-3 py-2 bg-gray-600 rounded"
                                placeholder="Section title"
                              />
                            </div>
                            <textarea
                              value={editingGuideItem.content || ''}
                              onChange={(e) => setEditingGuideItem({ ...editingGuideItem, content: e.target.value })}
                              className="w-full px-3 py-2 bg-gray-600 rounded h-48 resize-none text-sm"
                              placeholder="Section content..."
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setEditingGuideItem(null)}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm"
                              >
                                ✕ Cancel
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const formData = new FormData();
                                    formData.append('title', editingGuideItem.title);
                                    formData.append('icon', editingGuideItem.icon);
                                    formData.append('content', editingGuideItem.content || '');
                                    const res = await fetch(`${API}/api/travel-tools/admin/guide/items/${item.id}`, {
                                      method: 'PUT',
                                      headers: authHeaders,
                                      body: formData
                                    });
                                    if (res.ok) {
                                      setGuideItems(guideItems.map(g => g.id === item.id ? editingGuideItem : g));
                                      setEditingGuideItem(null);
                                      showMessage('Section updated!');
                                    }
                                  } catch (error) {
                                    showMessage('Failed to update');
                                  }
                                }}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-sm"
                              >
                                ✓ Save Changes
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div
                              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-650"
                              onClick={() => setExpandedGuideItem(expandedGuideItem === item.id ? null : item.id)}
                            >
                              <span className="text-2xl w-10 text-center">{item.icon}</span>
                              <span className="flex-1 font-medium">{item.title}</span>
                              <span className="text-gray-500 text-sm">#{index + 1}</span>
                              <span className="text-gray-400 text-sm">
                                {expandedGuideItem === item.id ? '▼' : '▶'}
                              </span>
                              {/* Up / Down reorder buttons — disabled at the boundaries.
                                  Calls /admin/guide/items/reorder with the full new ID order. */}
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (index === 0) return;
                                  const next = guideItems.slice();
                                  [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                  const prev = guideItems;
                                  setGuideItems(next); // optimistic
                                  try {
                                    const fd = new FormData();
                                    fd.append('order', JSON.stringify(next.map(g => g.id)));
                                    const res = await fetch(`${API}/api/travel-tools/admin/guide/items/reorder`, {
                                      method: 'PUT', headers: authHeaders, body: fd
                                    });
                                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                    showMessage('Moved up');
                                  } catch (err) {
                                    setGuideItems(prev); // rollback
                                    showMessage('Reorder failed — backend may need restart');
                                  }
                                }}
                                disabled={index === 0}
                                title="Move up"
                                className="px-2 py-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-30 disabled:cursor-not-allowed rounded text-xs"
                              >
                                ▲
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (index >= guideItems.length - 1) return;
                                  const next = guideItems.slice();
                                  [next[index + 1], next[index]] = [next[index], next[index + 1]];
                                  const prev = guideItems;
                                  setGuideItems(next); // optimistic
                                  try {
                                    const fd = new FormData();
                                    fd.append('order', JSON.stringify(next.map(g => g.id)));
                                    const res = await fetch(`${API}/api/travel-tools/admin/guide/items/reorder`, {
                                      method: 'PUT', headers: authHeaders, body: fd
                                    });
                                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                    showMessage('Moved down');
                                  } catch (err) {
                                    setGuideItems(prev); // rollback
                                    showMessage('Reorder failed — backend may need restart');
                                  }
                                }}
                                disabled={index >= guideItems.length - 1}
                                title="Move down"
                                className="px-2 py-1 bg-gray-600 hover:bg-gray-500 disabled:opacity-30 disabled:cursor-not-allowed rounded text-xs"
                              >
                                ▼
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingGuideItem({ ...item }); }}
                                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Delete "${item.title}"?`)) return;
                                  try {
                                    const res = await fetch(`${API}/api/travel-tools/admin/guide/items/${item.id}`, {
                                      method: 'DELETE',
                                      headers: authHeaders
                                    });
                                    if (res.ok) {
                                      setGuideItems(guideItems.filter(g => g.id !== item.id));
                                      showMessage('Section deleted!');
                                    }
                                  } catch (error) {
                                    showMessage('Failed to delete');
                                  }
                                }}
                                className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm"
                              >
                                🗑️
                              </button>
                            </div>
                            {expandedGuideItem === item.id && item.content && (
                              <div className="px-4 pb-4 pt-1 border-t border-gray-600">
                                <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">{item.content}</pre>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ CONFIG TAB (EMPTY - FOR FUTURE USE) ============ */}
          {activeTab === 'widgets' && (
            <AdminTravelToolsWidgets
              API={API}
              authHeaders={authHeaders}
              onMessage={(m) => setMessage(m)}
            />
          )}

          {activeTab === 'distance' && (
            <AdminTravelToolsDistance
              API={API}
              authHeaders={authHeaders}
              onMessage={(m) => setMessage(m)}
            />
          )}

          {activeTab === 'guide_cards' && (
            <AdminTravelToolsGuideCards
              API={API}
              authHeaders={authHeaders}
              onMessage={(m) => setMessage(m)}
            />
          )}

          {activeTab === 'config' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-xl p-6 border border-gray-600">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">⚙️</span>
                  <div>
                    <h2 className="text-2xl font-bold">Configuration</h2>
                    <p className="text-gray-400 text-sm">Reserved for future features</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-12 text-center">
                <span className="text-8xl block mb-6">🔧</span>
                <h3 className="text-2xl font-bold text-gray-400 mb-2">No Additional Configuration Needed</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  All features have been moved to their dedicated tabs. This space is reserved for any future configuration options.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Image Picker modal — single instance, routed by imagePickerTarget.kind to the right field */}
      <AdminImagePicker
        open={imagePickerOpen}
        onClose={() => { setImagePickerOpen(false); setImagePickerTarget(null); }}
        onPick={(asset) => {
          const url = asset?.url || '';
          if (!url) return;
          const t = imagePickerTarget;
          if (!t) return;
          if (t.kind === 'newEmoji') setNewEmoji(prev => ({ ...prev, image_url: url }));
          else if (t.kind === 'editEmoji') setEditingEmoji(prev => prev ? ({ ...prev, image_url: url }) : prev);
          else if (t.kind === 'newCategory') setNewPhraseCategory(prev => ({ ...prev, image_url: url }));
          else if (t.kind === 'editCategory') setEditingPhraseCategory(prev => prev ? ({ ...prev, image_url: url }) : prev);
        }}
        API={API}
        authHeaders={authHeaders}
        title="Pick from Image Manager"
      />
    </div>
  );
};

// Helper Components
const StatCard = ({ label, value, icon, color = 'blue' }) => (
  <div className={`bg-gray-800 rounded-xl p-4 border-l-4 border-${color}-500`}>
    <div className="flex items-center gap-2">
      <span className="text-2xl">{icon}</span>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-gray-400">{label}</p>
      </div>
    </div>
  </div>
);

export default AdminTravelTools;
